export interface ElementState {
  elements: Element[];
  selectedElement: Element | null;
  isDoubleSelected: boolean;
  manipulatingElement: boolean;
  undoStack: Array<{ type: string; element: Element }>;
  redoStack: Array<Element>;
  progressBars: { [key: string]: number };
}

export interface Element {
  id?: number;
  type: string;
  x: number;
  y: number;
  width: number;
  height: number;
  rotation: number;
  data: {};
  deleted: boolean;
}

export interface VideoElement extends Element {
  data: {
    state?: 'playing' | 'paused';
    lastSeeked?: 0;
  };
}

export interface YoutubeElement extends Element {
  data: {
    state?: 'playing' | 'paused';
  };
}

/* ACTIONS */

export enum ElementActionsKeys {
  LOAD_ELEMENTS = 'LOAD_ELEMENTS',
  ADD_ELEMENT = 'ADD_ELEMENT',
  UPLOAD_ELEMENT = 'UPLOAD_ELEMENT',
  UPDATE_ELEMENT = 'UPDATE_ELEMENT',
  SELECT_ELEMENT = 'SELECT_ELEMENT',
  UNDO = 'UNDO',
  REDO = 'REDO',
  CREATE_PROGRESS_BAR = 'CREATE_PROGRESS_BAR',
  UPDATE_PROGRESS_BAR = 'UPDATE_PROGRESS_BAR',
  DELETE_PROGRESS_BAR = 'DELETE_PROGRESS_BAR',
  SET_DOUBLE_SELECTED = 'SET_DOUBLE_SELECTED',
  SET_MANIPULATING_ELEMENT = 'SET_MANIPULATING_ELEMENT',
  SOCKET_ON_CREATED_ELEMENT = 'SOCKET_ON_CREATED_ELEMENT',
  SOCKET_ON_UPDATED_ELEMENT = 'SOCKET_ON_UPDATED_ELEMENT',
  RESET_STATE = 'RESET_STATE',
}

export interface ElementActionsPayload {
  [ElementActionsKeys.LOAD_ELEMENTS]: void;
  [ElementActionsKeys.ADD_ELEMENT]: Element;
  [ElementActionsKeys.UPLOAD_ELEMENT]: { file: File; element: Element; changeEl?: boolean };
  [ElementActionsKeys.UPDATE_ELEMENT]: Element | [Element, boolean];
  [ElementActionsKeys.SELECT_ELEMENT]: Element | null;
  [ElementActionsKeys.UNDO]: void;
  [ElementActionsKeys.REDO]: void;
  [ElementActionsKeys.CREATE_PROGRESS_BAR]: { id: string };
  [ElementActionsKeys.UPDATE_PROGRESS_BAR]: { id: string; progress: number };
  [ElementActionsKeys.DELETE_PROGRESS_BAR]: { id: string };
  [ElementActionsKeys.SET_DOUBLE_SELECTED]: boolean;
  [ElementActionsKeys.SET_MANIPULATING_ELEMENT]: boolean;
  [ElementActionsKeys.SOCKET_ON_CREATED_ELEMENT]: { element: Element };
  [ElementActionsKeys.SOCKET_ON_UPDATED_ELEMENT]: { element: Element };
  [ElementActionsKeys.RESET_STATE]: void;
}

/* MUTATIONS */

export enum ElementMutationsKeys {
  ADD_ELEMENT = 'ADD_ELEMENT',
  UPDATE_ELEMENT = 'UPDATE_ELEMENT',
  SELECT_ELEMENT = 'SELECT_ELEMENT',
  UNDO = 'UNDO',
  ADD_UNDO = 'ADD_UNDO',
  REDO = 'REDO',
  ADD_REDO = 'ADD_REDO',
  CREATE_PROGRESS_BAR = 'CREATE_PROGRESS_BAR',
  UPDATE_PROGRESS_BAR = 'UPDATE_PROGRESS_BAR',
  DELETE_PROGRESS_BAR = 'DELETE_PROGRESS_BAR',
  SET_DOUBLE_SELECTED = 'SET_DOUBLE_SELECTED',
  SET_MANIPULATING_ELEMENT = 'SET_MANIPULATING_ELEMENT',
  SET_ELEMENTS = 'SET_ELEMENTS',
  ELEMENT_RESET_STATE = 'ELEMENT_RESET_STATE',
}

export interface ElementMutationsPayload {
  [ElementMutationsKeys.ADD_ELEMENT]: Element;
  [ElementMutationsKeys.UPDATE_ELEMENT]: Element;
  [ElementMutationsKeys.SELECT_ELEMENT]: Element | null;
  [ElementMutationsKeys.UNDO]: void;
  [ElementMutationsKeys.ADD_UNDO]: { type: string; element: Element; redo?: boolean };
  [ElementMutationsKeys.REDO]: void;
  [ElementMutationsKeys.ADD_REDO]: Element;
  [ElementMutationsKeys.CREATE_PROGRESS_BAR]: { id: string };
  [ElementMutationsKeys.UPDATE_PROGRESS_BAR]: { id: string; progress: number };
  [ElementMutationsKeys.DELETE_PROGRESS_BAR]: { id: string };
  [ElementMutationsKeys.SET_DOUBLE_SELECTED]: boolean;
  [ElementMutationsKeys.SET_MANIPULATING_ELEMENT]: boolean;
  [ElementMutationsKeys.SET_ELEMENTS]: Element[];
  [ElementMutationsKeys.ELEMENT_RESET_STATE]: void;
}

/* GETTERS */

export enum ElementGettersKeys {
  boardElements = 'boardElements',
  allElements = 'allElements',
  selectedElement = 'selectedElement',
  isDoubleSelected = 'isDoubleSelected',
  isManipulatingElement = 'isManipulatingElement',
  biggestZIndex = 'biggestZIndex',
  smallestZIndex = 'smallestZIndex',
  haveUndo = 'haveUndo',
  haveRedo = 'haveRedo',
  progressBars = 'progressBars',
}

export interface ElementGettersReturns {
  [ElementGettersKeys.boardElements]: Element[];
  [ElementGettersKeys.allElements]: Element[];
  [ElementGettersKeys.selectedElement]: Element | null;
  [ElementGettersKeys.isDoubleSelected]: boolean;
  [ElementGettersKeys.isManipulatingElement]: boolean;
  [ElementGettersKeys.biggestZIndex]: number;
  [ElementGettersKeys.smallestZIndex]: number;
  [ElementGettersKeys.haveUndo]: boolean;
  [ElementGettersKeys.haveRedo]: boolean;
  [ElementGettersKeys.progressBars]: ElementState['progressBars'];
}
